/**
 * Zod schemas for all API input validation.
 *
 * Every Express route validates its request body / query against one of
 * these schemas BEFORE touching Firestore. This guarantees inputs are
 * shape-correct, type-coerced, and bounded.
 */
import { z } from 'zod';
import { CUSTOMIZATION_GROUP_TYPES, FULFILLMENT_METHODS, ORDER_STATUSES, PAYMENT_STATUSES, USER_ROLES, } from './constants.js';
// ─── Primitives ──────────────────────────────────────────────────────────────
export const centsSchema = z.number().int().nonnegative();
export const isoDateStringSchema = z.string().datetime();
// ─── User ────────────────────────────────────────────────────────────────────
export const userRoleSchema = z.enum(USER_ROLES);
export const updateUserProfileSchema = z
    .object({
    displayName: z.string().min(1).max(80).optional(),
    roomNumber: z.string().min(1).max(20).optional(),
    allergies: z.string().max(280).optional(),
    favoriteItemIds: z.array(z.string().min(1)).max(50).optional(),
})
    .strict();
export const updateUserRoleSchema = z.object({ role: userRoleSchema }).strict();
// ─── Menu ────────────────────────────────────────────────────────────────────
export const customizationOptionSchema = z
    .object({
    optionId: z.string().min(1),
    label: z.string().min(1).max(40),
    priceDeltaCents: z.number().int(),
    isDefault: z.boolean(),
})
    .strict();
export const customizationGroupSchema = z
    .object({
    groupId: z.string().min(1),
    name: z.string().min(1).max(40),
    type: z.enum(CUSTOMIZATION_GROUP_TYPES),
    required: z.boolean(),
    options: z.array(customizationOptionSchema).min(1),
    /**
     * Optional conditional visibility — see ShopSettings type doc.
     * Stored on the menu doc; consumed by the order modal and by
     * OrderService when computing whether required groups are
     * satisfied for a given selection.
     */
    dependsOn: z
        .object({
        groupId: z.string().min(1),
        optionIds: z.array(z.string().min(1)).min(1),
    })
        .strict()
        .optional(),
})
    .strict();
export const createMenuItemSchema = z
    .object({
    name: z.string().min(1).max(80),
    description: z.string().max(280),
    categoryId: z.string().min(1),
    basePriceCents: centsSchema,
    imageUrl: z.string().url().nullable().default(null),
    isAvailable: z.boolean().default(true),
    isSeasonal: z.boolean().default(false),
    customizationGroups: z.array(customizationGroupSchema).default([]),
    displayOrder: z.number().int().nonnegative().default(0),
})
    .strict();
export const updateMenuItemSchema = createMenuItemSchema.partial().strict();
export const createCategorySchema = z
    .object({
    name: z.string().min(1).max(40),
    displayOrder: z.number().int().nonnegative().default(0),
    isActive: z.boolean().default(true),
    icon: z.string().max(40).default(''),
})
    .strict();
export const updateCategorySchema = createCategorySchema.partial().strict();
// ─── Orders ──────────────────────────────────────────────────────────────────
export const orderStatusSchema = z.enum(ORDER_STATUSES);
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);
export const fulfillmentMethodSchema = z.enum(FULFILLMENT_METHODS);
export const selectedCustomizationInputSchema = z
    .object({
    groupId: z.string().min(1),
    optionIds: z.array(z.string().min(1)).min(1),
})
    .strict();
export const orderItemInputSchema = z
    .object({
    itemId: z.string().min(1),
    quantity: z.number().int().min(1).max(20),
    selectedCustomizations: z.array(selectedCustomizationInputSchema).default([]),
    specialInstructions: z.string().max(280).default(''),
})
    .strict();
export const createOrderSchema = z
    .object({
    items: z.array(orderItemInputSchema).min(1).max(20),
    /** Tip amount in cents. 0 if no tip selected; capped at $500 so a
     *  fat-fingered or abusive value can't poison totals/analytics (and, once
     *  real Square is wired, can't become an unbounded charge). */
    tipCents: centsSchema.max(50_000).default(0),
    /** Optional per-order room override. Defaults to the teacher's saved roomNumber. */
    teacherRoom: z.string().min(1).max(20).optional(),
    /** Pickup at counter vs delivery to room. Defaults to delivery. */
    fulfillmentMethod: fulfillmentMethodSchema.default('delivery'),
    specialInstructions: z.string().max(280).default(''),
})
    .strict();
export const updateOrderStatusSchema = z.object({ status: orderStatusSchema }).strict();
export const orderListQuerySchema = z
    .object({
    status: orderStatusSchema.optional(),
    teacherId: z.string().optional(),
    fromDate: isoDateStringSchema.optional(),
    toDate: isoDateStringSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
/**
 * Admin dashboard analytics query. `windowDays` is the look-back window;
 * missing or out-of-range input falls back to 30 rather than erroring, so a
 * stray query string can't 400 the dashboard.
 */
export const analyticsQuerySchema = z.object({
    windowDays: z.coerce.number().int().min(1).max(365).catch(30).default(30),
});
// ─── Settings ────────────────────────────────────────────────────────────────
export const updateShopSettingsSchema = z
    .object({
    isOpen: z.boolean().optional(),
    taxRate: z.number().min(0).max(1).optional(),
    printerIp: z.string().ip().optional(),
    printerPort: z.number().int().min(1).max(65535).optional(),
    squareSandboxMode: z.boolean().optional(),
    estimatedPrepMinutes: z.number().int().min(1).max(120).optional(),
})
    .strict();
/**
 * The barista-accessible toggle endpoint accepts only this — a single
 * boolean. Wider settings (tax, printer, etc.) stay admin-only via
 * `updateShopSettingsSchema`.
 */
export const setShopOpenSchema = z.object({ isOpen: z.boolean() }).strict();
// ─── Square ──────────────────────────────────────────────────────────────────
export const createSquarePaymentSchema = z
    .object({
    orderId: z.string().min(1),
    /**
     * Square payment-source nonce from the Web Payments SDK.
     * In sandbox mode the server accepts the literal "cnon:mock".
     */
    sourceId: z.string().min(1),
})
    .strict();
//# sourceMappingURL=schemas.js.map